import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Newspaper, RefreshCw, ArrowUpRight } from 'lucide-react';

const FinanceNewsCard = () => {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API}/news/finance`, { timeout: 25000 });
      setData(r.data);
    } catch {
      // silent
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30 * 60 * 1000); // 30 min
    return () => clearInterval(t);
  }, []);

  return (
    <div className="finance-news-card" data-testid="finance-news-card">
      <div className="fn-head">
        <div className="fn-title">
          <div className="fn-icon"><Newspaper size={16} /></div>
          <div>
            <div className="fn-eyebrow">
              <span className="fn-live-dot" /> LIVE FEED
            </div>
            <div className="fn-h">Today's Finance Pulse</div>
          </div>
        </div>
        <button onClick={load} className="fn-refresh" title="Refresh" data-testid="finance-news-refresh" aria-label="Refresh">
          <RefreshCw size={13} style={{ animation: refreshing ? 'fn-spin 0.8s linear infinite' : 'none' }} />
        </button>
      </div>

      <div className="fn-list" data-testid="finance-news-list">
        {(data?.bullets || Array.from({ length: 5 })).map((b, i) => (
          <div key={i} className="fn-item" data-testid={b ? `finance-news-${b.category.toLowerCase()}` : `finance-news-skeleton-${i}`}>
            {b ? (
              <>
                <span className="fn-tag" style={{ background: `${b.tag_color}18`, color: b.tag_color, borderColor: `${b.tag_color}30` }}>
                  {b.category}
                </span>
                <span className="fn-text">{b.headline}</span>
              </>
            ) : (
              <span className="fn-skeleton" />
            )}
          </div>
        ))}
      </div>

      <div className="fn-foot">
        <span>{data?.generated_at ? `Updated ${new Date(data.generated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : 'Updating…'}</span>
        <a href="/tax-services" className="fn-cta">
          File ITR / GST <ArrowUpRight size={12} />
        </a>
      </div>

      <style>{`
        .finance-news-card {
          background: white;
          border: 1px solid rgba(15, 23, 42, 0.06);
          border-radius: 18px;
          padding: 1.1rem 1.15rem;
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.05);
          width: 100%;
          max-width: 420px;
          backdrop-filter: blur(8px);
        }
        .fn-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 0.85rem; }
        .fn-title { display: flex; align-items: center; gap: 0.65rem; }
        .fn-icon { width: 36px; height: 36px; border-radius: 10px; background: linear-gradient(135deg, #FF6B35 0%, #F97316 100%); color: white; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 10px rgba(255, 107, 53, 0.32); }
        .fn-eyebrow { font-size: 0.62rem; color: #EF4444; font-weight: 800; letter-spacing: 0.1em; display: inline-flex; align-items: center; gap: 4px; }
        .fn-live-dot { width: 7px; height: 7px; border-radius: 50%; background: #EF4444; animation: fn-blink 1.4s ease-in-out infinite; box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6); }
        .fn-h { font-size: 1rem; color: #0F172A; font-weight: 800; line-height: 1.1; }
        .fn-refresh {
          background: #F1F5F9; border: 1px solid #E2E8F0; color: #475569; width: 30px; height: 30px;
          border-radius: 8px; display: flex; align-items: center; justify-content: center; cursor: pointer;
        }
        .fn-refresh:hover { background: #E2E8F0; }

        .fn-list { display: flex; flex-direction: column; gap: 0.55rem; }
        .fn-item {
          display: flex; align-items: flex-start; gap: 0.55rem; padding: 0.55rem 0.6rem;
          background: #F8FAFC; border-radius: 10px; border: 1px solid #F1F5F9;
          transition: background 0.15s ease;
          animation: fn-slide-in 0.4s ease-out;
        }
        .fn-item:hover { background: #F1F5F9; }
        .fn-tag {
          font-size: 0.62rem; font-weight: 800; letter-spacing: 0.04em; padding: 3px 7px;
          border-radius: 999px; border: 1px solid; white-space: nowrap; flex-shrink: 0;
          margin-top: 1px;
        }
        .fn-text { color: #0F172A; font-size: 0.82rem; line-height: 1.4; }
        .fn-skeleton {
          display: block; width: 100%; height: 14px; border-radius: 4px;
          background: linear-gradient(90deg, #F1F5F9 0%, #E2E8F0 50%, #F1F5F9 100%);
          background-size: 200% 100%;
          animation: fn-shimmer 1.4s linear infinite;
        }
        .fn-foot { display: flex; align-items: center; justify-content: space-between; margin-top: 0.85rem; padding-top: 0.75rem; border-top: 1px dashed #E2E8F0; font-size: 0.7rem; color: #94A3B8; }
        .fn-cta { color: #10B981; font-weight: 700; text-decoration: none; display: inline-flex; align-items: center; gap: 3px; }
        .fn-cta:hover { color: #059669; }

        @keyframes fn-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes fn-spin { to { transform: rotate(360deg); } }
        @keyframes fn-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @keyframes fn-slide-in { 0% { opacity: 0; transform: translateY(6px); } 100% { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default FinanceNewsCard;
